import { z } from 'zod';

// Shared building blocks for block schemas.

// URL fields used as placeholders accept an empty string (owner uploads
// or fills in the real value later through the inspector). When present
// and non-empty, the renderer assumes it is a valid URL — validation
// at upload / paste time is the inspector's job, not the storage
// schema's. AI-generated pages routinely emit "" for image URLs.
const placeholderUrlSchema = z.union([z.string().url(), z.literal('')]);

const imageRefSchema = z.object({
  url: placeholderUrlSchema,
  alt: z.string().default(''),
});

const ctaSchema = z.object({
  label: z.string().min(1),
  // href can be a real URL, an anchor like "#contact", or a placeholder
  // empty string the owner fills in. min(1) used to require at least
  // one char; we now allow empty strings so AI output validates.
  href: z.string(),
});

// =====================================================================
// Block prop schemas — one Zod schema per block type.
// =====================================================================

export const heroPropsSchema = z.object({
  eyebrow: z.string().optional(),
  headline: z.string().min(1),
  subhead: z.string().optional(),
  primaryCta: ctaSchema.optional(),
  secondaryCta: ctaSchema.optional(),
  image: imageRefSchema.optional(),
  layout: z.enum(['centered', 'split-left', 'split-right']).default('centered'),
});

export const servicesPropsSchema = z.object({
  title: z.string().optional(),
  items: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        icon: z.string().optional(),
        // Optional price chip per service. Used by service-business
        // "menu" pricing (e.g. salon, cleaning, photography). Free-form
        // string so "$120", "$60-90", "From $200", "Free quote" all
        // work without a dedicated currency / range schema.
        price: z.string().optional(),
      }),
    )
    .min(1),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
});

export const pricingPropsSchema = z.object({
  title: z.string().optional(),
  subtitle: z.string().optional(),
  columns: z.union([z.literal(2), z.literal(3)]).default(3),
  tiers: z
    .array(
      z.object({
        name: z.string().min(1),
        price: z.string().min(1),
        period: z.string().optional(),
        description: z.string().optional(),
        features: z.array(z.string().min(1)).default([]),
        cta: ctaSchema,
        highlighted: z.boolean().default(false),
      }),
    )
    .min(1),
});

export const galleryPropsSchema = z.object({
  title: z.string().optional(),
  images: z.array(imageRefSchema).min(1),
  layout: z.enum(['grid', 'masonry', 'carousel']).default('grid'),
});

export const contactPropsSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  // Email + URL fields accept empty strings as placeholders so
  // AI-generated pages validate; real-value validation happens at the
  // inspector edit boundary.
  email: z.union([z.string().email(), z.literal('')]).optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  formEnabled: z.boolean().default(true),
  mapEmbedUrl: placeholderUrlSchema.optional(),
});

export const aboutPropsSchema = z.object({
  title: z.string().optional(),
  body: z.string().min(1),
  image: imageRefSchema.optional(),
});

export const testimonialsPropsSchema = z.object({
  title: z.string().optional(),
  items: z
    .array(
      z.object({
        quote: z.string().min(1),
        author: z.string().min(1),
        role: z.string().optional(),
        avatar: imageRefSchema.optional(),
      }),
    )
    .min(1),
});

export const ctaPropsSchema = z.object({
  headline: z.string().min(1),
  subhead: z.string().optional(),
  primaryCta: ctaSchema,
  background: z.enum(['neutral', 'brand', 'image']).default('brand'),
});

export const headingPropsSchema = z.object({
  text: z.string().min(1),
  level: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(2),
  align: z.enum(['left', 'center', 'right']).default('left'),
});

export const textPropsSchema = z.object({
  body: z.string().min(1),
  align: z.enum(['left', 'center']).default('left'),
});

export const faqPropsSchema = z.object({
  title: z.string().optional(),
  items: z
    .array(
      z.object({
        question: z.string().min(1),
        answer: z.string().min(1),
      }),
    )
    .min(1),
});

export const teamPropsSchema = z.object({
  title: z.string().optional(),
  members: z
    .array(
      z.object({
        name: z.string().min(1),
        role: z.string().optional(),
        bio: z.string().optional(),
        photo: imageRefSchema.optional(),
      }),
    )
    .min(1),
});

// Trust-strip block. Renders a horizontal row of short credential
// chips (icon + label) — "Licensed", "Insured", "4.9★ Google",
// "Since 2008", "Family-owned". Used right under hero on
// service-business pages to build credibility before pricing.
export const credentialsPropsSchema = z.object({
  items: z
    .array(
      z.object({
        // Free-form: emoji ("⭐"), short text ("4.9★"), or lucide
        // icon name handled in the editor. Renderer outputs as-is.
        icon: z.string().optional(),
        label: z.string().min(1),
      }),
    )
    .min(1),
});

// Multi-location phase (Scenario B, docs/plans/2026-08-09-multi-location-design.md,
// Task 9). Deliberately carries NO member data — the block is a pointer, not a
// cache. The renderer resolves the org's OTHER grouped location-sites' NAP at
// render time (server-derived, org-scoped) so member data can never go stale in
// stored props and can never leak across tenants via a client-supplied payload.
export const locationsPropsSchema = z.object({
  heading: z.string().min(1).default('Our locations'),
  showPhones: z.boolean().default(true),
});

export const footerPropsSchema = z.object({
  businessName: z.string().min(1),
  tagline: z.string().optional(),
  links: z.array(ctaSchema).default([]),
  copyrightYear: z.number().int().optional(),
  socialLinks: z
    .array(
      z.object({
        platform: z.enum(['twitter', 'instagram', 'facebook', 'linkedin', 'tiktok', 'youtube']),
        href: placeholderUrlSchema,
      }),
    )
    .default([]),
});

// Discriminated union of all blocks.
export const blockSchema = z.discriminatedUnion('type', [
  z.object({ id: z.string(), type: z.literal('hero'), props: heroPropsSchema }),
  z.object({ id: z.string(), type: z.literal('services'), props: servicesPropsSchema }),
  z.object({ id: z.string(), type: z.literal('pricing'), props: pricingPropsSchema }),
  z.object({ id: z.string(), type: z.literal('gallery'), props: galleryPropsSchema }),
  z.object({ id: z.string(), type: z.literal('contact'), props: contactPropsSchema }),
  z.object({ id: z.string(), type: z.literal('about'), props: aboutPropsSchema }),
  z.object({ id: z.string(), type: z.literal('testimonials'), props: testimonialsPropsSchema }),
  z.object({ id: z.string(), type: z.literal('cta'), props: ctaPropsSchema }),
  z.object({ id: z.string(), type: z.literal('heading'), props: headingPropsSchema }),
  z.object({ id: z.string(), type: z.literal('text'), props: textPropsSchema }),
  z.object({ id: z.string(), type: z.literal('faq'), props: faqPropsSchema }),
  z.object({ id: z.string(), type: z.literal('team'), props: teamPropsSchema }),
  z.object({ id: z.string(), type: z.literal('credentials'), props: credentialsPropsSchema }),
  z.object({ id: z.string(), type: z.literal('locations'), props: locationsPropsSchema }),
  z.object({ id: z.string(), type: z.literal('footer'), props: footerPropsSchema }),
]);

export const pageBlocksSchema = z.array(blockSchema);

export type Block = z.infer<typeof blockSchema>;
export type PageBlocks = z.infer<typeof pageBlocksSchema>;
